import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import firebase from '../../../utilities/firebase';
import { StateManager } from '../../../utilities/stateManager';
import ExpenseModal from '../../../components/ExpenseModal';
import moment from 'moment';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function ExpensesPanel({ expenses = [], stockNumber, disabled }) {
  const [items, setItems] = React.useState(expenses);
  const [selectedExpense, setSelectedExpense] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);

  React.useEffect(() => { setItems(expenses); }, [expenses]);

  const total = items.reduce((s, e) => s + (e.amount || 0), 0);

  const viewReceipt = async (fileLocation) => {
    const url = await firebase.storage().ref(fileLocation).getDownloadURL();
    window.open(url, '_blank');
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    StateManager.setLoading(true);
    await firebase.firestore().collection('purchases').doc(id).delete();
    setItems(prev => prev.filter(x => x.id !== id));
    StateManager.setLoading(false);
  };

  const addExpense = () => {
    setSelectedExpense(null);
    setModalOpen(true);
  };

  return (
    <Box>
      {items.length > 0 ? (
        <Box>
          {items.map((exp, i) => (
            <Box
              key={exp.id || i}
              onClick={() => { setSelectedExpense(exp); setModalOpen(true); }}
              sx={{
                px: 2, py: 1.25,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                borderBottom: i < items.length - 1 ? 1 : 0, borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Checkbox
                  checked={exp.isPayable === false || exp.isPayable === 'false'}
                  size="small"
                  color="success"
                  onClick={(e) => e.stopPropagation()}
                  disableRipple
                  sx={{ p: 0.5, flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{exp.memo || '—'}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[exp.date ? moment(exp.date, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD']).format('MMM D, YYYY') : null, exp.vendor].filter(Boolean).join(' · ') || '—'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{USD(exp.amount)}</Typography>
                {exp.files?.[0] && (
                  <IconButton size="small" data-sold-ok onClick={(e) => { e.stopPropagation(); viewReceipt(exp.files[0]); }}>
                    <ReceiptIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteExpense(exp.id); }} disabled={disabled}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>{USD(total)}</Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>No expenses recorded</Typography>
        </Box>
      )}

      <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
        <Button size="small" startIcon={<AddIcon />} onClick={addExpense} disabled={disabled}>
          Add Expense
        </Button>
      </Box>

      <ExpenseModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedExpense(null); }}
        expense={selectedExpense}
        stockNumber={stockNumber}
        onSaved={(saved) => {
          if (selectedExpense) {
            setItems(prev => prev.map(t => t.id === saved.id ? { ...t, ...saved } : t));
          } else {
            setItems(prev => [...prev, saved]);
          }
        }}
      />
    </Box>
  );
}
